import React, { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import RetryIndicator from '../../components/ui/RetryIndicator';
import Button from '../../components/ui/Button';
import JobCard from './components/JobCard';
import JobFilters from './components/JobFilters';
import CreateJobModal from './components/CreateJobModal';
import MessageModal from './components/MessageModal';
import EmptyState from './components/EmptyState';
import { useAuth } from '../../contexts/AuthContext';
import jobService from '../../services/jobService';
import { supabase } from '../../lib/supabase';
import { useRetryState } from '../../hooks/useRetryState';
import { retryWithBackoff } from '../../utils/retryWithBackoff';

const ClientJobManagement = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [selectedJobForMessage, setSelectedJobForMessage] = useState(null);
  const [filters, setFilters] = useState({
    searchQuery: '',
    workType: 'all',
    location: 'all',
    budgetRange: 'all',
    status: 'all'
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { retryState, startRetry, updateRetry, completeRetry, resetRetry } = useRetryState();

  useEffect(() => {
    if (userProfile?.id) {
      loadJobs();
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile?.id) {
      const channel = supabase?.channel('client-jobs')?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'jobs',
            filter: `client_id=eq.${userProfile?.id}`
          },
          (payload) => {
            if (payload?.eventType === 'INSERT') {
              loadJobs();
            } else if (payload?.eventType === 'UPDATE') {
              setJobs(prev => prev?.map(j => 
                j?.id === payload?.new?.id ? { ...j, ...payload?.new } : j
              ));
            } else if (payload?.eventType === 'DELETE') {
              setJobs(prev => prev?.filter(j => j?.id !== payload?.old?.id));
            }
          }
        )?.subscribe();

      return () => supabase?.removeChannel(channel);
    }
  }, [userProfile]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      startRetry();
      const data = await retryWithBackoff(
        () => jobService?.getJobsByClient(userProfile?.id),
        {},
        updateRetry
      );
      setJobs(data);
      completeRetry(true);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError(err?.message);
      completeRetry(false, err?.message);
    } finally {
      setLoading(false);
      setTimeout(resetRetry, 3000);
    }
  };

  const handleCreateJob = async (jobData) => {
    try {
      startRetry();
      await retryWithBackoff(
        () => jobService?.createJob(jobData),
        {},
        updateRetry
      );
      setIsCreateModalOpen(false);
      completeRetry(true);
      loadJobs();
    } catch (err) {
      console.error('Error creating job:', err);
      completeRetry(false, err?.message);
      alert('Fout bij het aanmaken van opdracht: ' + err?.message);
    } finally {
      setTimeout(resetRetry, 3000);
    }
  };

  const filteredJobs = useMemo(() => {
    return jobs?.filter(job => {
      if (activeTab === 'active' && job?.status !== 'open' && job?.status !== 'ongoing') return false;
      if (activeTab === 'completed' && job?.status !== 'completed') return false;
      if (activeTab === 'archived' && job?.status !== 'cancelled') return false;

      if (filters?.searchQuery && !job?.title?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase())) {
        return false;
      }

      if (filters?.workType !== 'all' && job?.work_type !== filters?.workType) {
        return false;
      }

      if (filters?.location !== 'all' && !job?.location?.includes(filters?.location)) {
        return false;
      }

      if (filters?.status !== 'all' && job?.status !== filters?.status) {
        return false;
      }

      return true;
    });
  }, [jobs, activeTab, filters]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Laden...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <RetryIndicator 
            isRetrying={retryState?.isRetrying}
            attempt={retryState?.attempt}
            maxRetries={retryState?.maxRetries}
            delay={retryState?.delay}
            error={retryState?.error}
            success={retryState?.success}
            className="mb-4"
          />
          <div className="space-y-6 md:space-y-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-2">
                  Personeelsvragen Beheer
                </h1>
                <p className="text-muted-foreground">
                  Beheer uw personeelsvragen en vind de juiste vakmensen voor uw projecten
                </p>
              </div>
              <Button 
                iconName="Plus" 
                iconPosition="left"
                onClick={() => setIsCreateModalOpen(true)}
              >
                Nieuwe Personeelsvraag
              </Button>
            </div>

            <div 
              className="bg-white border-2 border-slate-200 rounded-lg p-4 md:p-6"
              style={{
                boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
              }}
            >
              <div className="flex items-center gap-4 mb-6 border-b border-border overflow-x-auto">
                <button
                  onClick={() => setActiveTab('active')}
                  className={`pb-3 px-4 font-medium whitespace-nowrap transition-smooth ${
                    activeTab === 'active' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Actieve Opdrachten ({jobs?.filter(j => j?.status === 'open' || j?.status === 'ongoing')?.length})
                </button>
                <button
                  onClick={() => setActiveTab('completed')}
                  className={`pb-3 px-4 font-medium whitespace-nowrap transition-smooth ${
                    activeTab === 'completed'
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Afgerond ({jobs?.filter(j => j?.status === 'completed')?.length})
                </button>
                <button
                  onClick={() => setActiveTab('archived')}
                  className={`pb-3 px-4 font-medium whitespace-nowrap transition-smooth ${
                    activeTab === 'archived' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Gearchiveerd ({jobs?.filter(j => j?.status === 'cancelled')?.length})
                </button>
              </div>

              <JobFilters filters={filters} onFilterChange={setFilters} onClearFilters={() => setFilters({
                searchQuery: '',
                workType: 'all',
                location: 'all',
                budgetRange: 'all',
                status: 'all'
              })} />

              <div className="space-y-4 mt-6">
                {filteredJobs?.length > 0 ? (
                  filteredJobs?.map((job) => (
                    <JobCard
                      key={job?.id}
                      job={{
                        ...job,
                        statusLabel: job?.status === 'open' ? 'Openstaand' : 
                                    job?.status === 'ongoing' ? 'In uitvoering' : 
                                    job?.status === 'completed' ? 'Afgerond' : 'Geannuleerd',
                        affiliateInfo: job?.affiliate ? {
                          name: job?.affiliate?.company_name,
                          commission: parseFloat(job?.budget) * (parseFloat(job?.affiliate?.commission_rate) / 100)
                        } : null
                      }}
                      onMessage={(job) => {
                        setSelectedJobForMessage(job);
                        setIsMessageModalOpen(true);
                      }}
                    />
                  ))
                ) : (
                  <EmptyState
                    type="no-results"
                    title="Geen opdrachten gevonden"
                    description="Er zijn geen opdrachten die aan uw filters voldoen."
                    actionLabel="Nieuwe Opdracht"
                    onAction={() => setIsCreateModalOpen(true)}
                    onCreateJob={() => setIsCreateModalOpen(true)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isCreateModalOpen && (
        <CreateJobModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateJob}
        />
      )}
      {isMessageModalOpen && selectedJobForMessage && (
        <MessageModal
          isOpen={isMessageModalOpen}
          jobId={selectedJobForMessage?.id}
          jobTitle={selectedJobForMessage?.title}
          job={selectedJobForMessage}
          onClose={() => {
            setIsMessageModalOpen(false);
            setSelectedJobForMessage(null);
          }}
        />
      )}
    </MainLayout>
  );
};

export default ClientJobManagement;